import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { formatDateTime, humanize } from '@/lib/format';
import { Card, ErrorState, Money, Spinner, StatusBadge } from '@/components/ui';

export function InvoiceApprovalPanel({
  invoiceId,
  approvalId,
  currentApprovalId,
}: {
  invoiceId: string;
  approvalId: string;
  currentApprovalId?: string;
}) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');
  const canReadApproval = can('approval:read') || can('approval:read_all');

  const approvalQuery = useQuery({
    queryKey: ['approval', approvalId],
    queryFn: () => api.approvals.get(approvalId),
    enabled: canReadApproval && currentApprovalId === approvalId,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const decision = useMutation({
    mutationFn: (action: 'APPROVE' | 'REJECT') =>
      api.approvals.act(approvalId, action, comment || undefined),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['approval', approvalId] }),
        queryClient.invalidateQueries({ queryKey: ['approvals'] }),
        queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] }),
        queryClient.invalidateQueries({ queryKey: ['invoices'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
  });

  if (currentApprovalId !== approvalId) {
    return (
      <Card className="border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 sm:col-span-2">
        This approval link no longer matches the invoice’s current approval request.
      </Card>
    );
  }
  if (!canReadApproval) {
    return (
      <Card className="p-4 text-sm text-slate-600 sm:col-span-2">
        Your account can view this invoice but does not have approval access.
      </Card>
    );
  }
  if (approvalQuery.isLoading) return <Spinner />;
  if (approvalQuery.error) return <ErrorState error={approvalQuery.error} />;

  const request = approvalQuery.data;
  if (!request) return null;
  if (request.subjectType !== 'VENDOR_INVOICE' || request.subjectId !== invoiceId) {
    return (
      <Card className="border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 sm:col-span-2">
        This approval request does not belong to this invoice.
      </Card>
    );
  }

  return (
    <Card className="sm:col-span-2">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 sm:px-5">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-semibold">Approval {request.reference}</h2>
          <StatusBadge status={request.status} />
          {request.ruleName ? (
            <span className="text-sm text-slate-500">{request.ruleName}</span>
          ) : null}
        </div>
        <p className="text-xs text-slate-500">Submitted {formatDateTime(request.requestedAt)}</p>
      </div>

      <ol className="divide-y divide-slate-100 px-4 sm:px-5">
        {request.steps.map((step) => (
          <li key={step.order} className="flex items-start gap-3 py-3">
            <span
              className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                step.status === 'APPROVED'
                  ? 'bg-emerald-100 text-emerald-700'
                  : step.status === 'REJECTED'
                    ? 'bg-red-100 text-red-700'
                    : step.status === 'ACTIVE'
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-slate-100 text-slate-500'
              }`}
            >
              {step.status === 'APPROVED' ? '✓' : step.status === 'REJECTED' ? '×' : step.order}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{step.label}</p>
              <p className="text-xs text-slate-500">
                {step.status === 'ACTIVE'
                  ? 'Awaiting decision'
                  : step.actedByName
                    ? `${humanize(step.status)} by ${step.actedByName} · ${formatDateTime(step.actedAt)}`
                    : humanize(step.status)}
              </p>
              {step.comment ? (
                <p className="mt-1 text-sm text-slate-600">“{step.comment}”</p>
              ) : null}
            </div>
          </li>
        ))}
      </ol>

      <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-4 sm:flex-row sm:items-end sm:justify-between sm:px-5">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">Requested approval</p>
          <p className="mt-1 font-semibold">
            <Money minor={request.amount} currency={request.currency} />
          </p>
        </div>
        {request.canAct ? (
          <div className="w-full sm:max-w-xl">
            <label className="label" htmlFor={`approval-comment-${approvalId}`}>
              Comment
            </label>
            <textarea
              id={`approval-comment-${approvalId}`}
              className="input"
              rows={2}
              placeholder="Optional for approval; recommended for rejection"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
            <div className="mt-2 flex gap-2">
              <button
                className="btn-primary flex-1"
                disabled={decision.isPending}
                onClick={() => decision.mutate('APPROVE')}
              >
                Approve
              </button>
              <button
                className="btn-danger flex-1"
                disabled={decision.isPending}
                onClick={() => decision.mutate('REJECT')}
              >
                Reject
              </button>
            </div>
            {decision.error ? (
              <div className="mt-2">
                <ErrorState error={decision.error} />
              </div>
            ) : null}
          </div>
        ) : request.status === 'IN_PROGRESS' ? (
          <p className="text-sm text-slate-600">
            This approval is not currently waiting on your account.
          </p>
        ) : (
          <p className="text-sm text-slate-600">This approval request is complete.</p>
        )}
      </div>
    </Card>
  );
}
